import { useEffect, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { FlowStatePill } from '@/components/flows/FlowStatePill'
import { getFlowState } from '@/components/flows/flow-state'
import { applyNodeChange } from '@/components/flows/inspector/apply-node-change'
import { LiveControlPanel } from '@/components/flows/inspector/live-control-panel'
import { RangeRow } from '@/components/flows/inspector/range-row'
import { useLiveControl } from '@/components/flows/inspector/use-live-control'
import {
  Button,
  InlineEmpty,
  InlineError,
  SectionCard,
  Skeleton,
  Switch,
} from '@/components/ui'
import { apiClient, scopedApiPath } from '@/lib/api-client'
import { parseGraph } from '@/lib/flow-graph'
import { cn } from '@/lib/utils'
import {
  appliesWithoutRestart,
  normalizePassthrough,
  passthroughSettings,
} from '@/lib/passthrough'
import { useViewAsStore } from '@/stores/view-as-store'
import type {
  FlowGraphConnection,
  FlowGraphNode,
  FlowListItem,
  FlowLiveSummary,
  FlowsListResponse,
  FlowSingleResponse,
} from '@/lib/types'

type LiveMixerSearch = { flow?: number; node?: number }
type Slot = 'a' | 'b'

type MixerListEntry = {
  nodeId: number
  name: string
  active: boolean
  blocked: boolean
}

type LayerFeed = {
  slot: Slot
  name: string
}

export const Route = createFileRoute('/admin/live-mixer')({
  validateSearch: (search: Record<string, unknown>): LiveMixerSearch => {
    const flow = Number(search.flow)
    const node = Number(search.node)
    return {
      ...(Number.isSafeInteger(flow) && flow > 0 ? { flow } : {}),
      ...(Number.isSafeInteger(node) && node > 0 ? { node } : {}),
    }
  },
  component: AdminLiveMixerPage,
})

function AdminLiveMixerPage() {
  const { flow, node } = Route.useSearch()

  if (flow === undefined && node === undefined) return <LiveMixerList />
  if (flow === undefined || node === undefined) {
    return (
      <MixerMessage
        title="Mixer not found"
        message="Choose a mixer from the Live mixer list. Both a flow and a mixer node are required."
      />
    )
  }

  return <LiveMixerDetail key={`${flow}:${node}`} flowId={flow} nodeId={node} />
}

function LiveMixerList() {
  const viewAsId = useViewAsStore((state) => state.viewAsId)
  const flowsQuery = useQuery({
    queryKey: ['admin', 'live-mixer', 'flows', viewAsId ?? 'self'] as const,
    queryFn: () => apiClient.get<FlowsListResponse>(scopedApiPath('/flows/', viewAsId)),
    refetchInterval: 3000,
  })

  if (flowsQuery.isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2" aria-busy="true">
        <Skeleton className="h-40" />
        <Skeleton className="h-40" />
      </div>
    )
  }

  if (flowsQuery.isError) {
    return (
      <div className="space-y-3">
        <InlineError message="Could not load live mixers." />
        <Button size="sm" variant="outline" onClick={() => void flowsQuery.refetch()}>
          Retry
        </Button>
      </div>
    )
  }

  const groups = (flowsQuery.data?.flows ?? [])
    .map((flow) => ({ flow, mixers: mixersForFlow(flow) }))
    .filter((group) => group.mixers.length > 0)

  return (
    <SectionCard
      title="Live mixers"
      description="Pick a mixer to change which layers are visible, move them, or swap them while the flow is on air."
      data-testid="admin-live-mixer-list"
    >
      {groups.length === 0 ? (
        <InlineEmpty message="No live mixers yet. A mixer appears here when a flow with a two-input Mix node is armed." />
      ) : (
        <div className={cn('grid gap-4 sm:grid-cols-2', groups.length >= 3 && 'xl:grid-cols-3')}>
          {groups.map(({ flow, mixers }) => (
            <div key={flow.ID} className="rounded-lg border border-border/60 bg-background/40 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-semibold" title={flow.NAME}>{flow.NAME}</h3>
                  <p className="text-xs text-muted-foreground">Flow <span className="font-mono">{flow.ID}</span></p>
                </div>
                <FlowStatePill state={getFlowState(flow)} />
              </div>
              <ul className="space-y-2">
                {mixers.map((mixer) => (
                  <li key={mixer.nodeId}>
                    <Link
                      to="/admin/live-mixer"
                      search={{ flow: flow.ID, node: mixer.nodeId }}
                      className="flex items-center justify-between gap-3 rounded-md border border-border/40 px-3 py-2 transition-colors hover:border-cyber-cyan/50 hover:bg-cyber-cyan/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyber-cyan/70"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">{mixer.name}</span>
                        <span className="block text-xs text-muted-foreground">
                          Mix node <span className="font-mono">{mixer.nodeId}</span>
                        </span>
                      </span>
                      <StatePill
                        tone={mixer.blocked ? 'amber' : mixer.active ? 'cyan' : 'muted'}
                        label={mixer.blocked ? 'Blocked' : mixer.active ? 'Running' : 'Stopped'}
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  )
}

function LiveMixerDetail({ flowId, nodeId }: { flowId: number; nodeId: number }) {
  const viewAsId = useViewAsStore((state) => state.viewAsId)
  const flowQuery = useQuery({
    queryKey: ['admin', 'live-mixer', 'flow', flowId, viewAsId ?? 'self'] as const,
    queryFn: () => apiClient.get<FlowSingleResponse>(
      scopedApiPath(`/flows/?id=${flowId}`, viewAsId),
    ),
    refetchInterval: 3000,
  })

  if (flowQuery.isLoading) return <Skeleton className="h-96" />

  if (flowQuery.isError || !flowQuery.data?.flow) {
    return (
      <MixerMessage
        title="Flow not found"
        message="This flow could not be loaded. It may no longer exist or you may not have access to it."
        onRetry={() => void flowQuery.refetch()}
      />
    )
  }

  const flow = flowQuery.data.flow
  const graph = parseGraph(flow.GRAPH)
  const node = graph.nodes.find((candidate) => candidate.id === nodeId)

  if (!node || node.type !== 'passthrough') {
    return (
      <MixerMessage
        title="Mixer not found"
        message="The selected node does not exist or is not a two-input Mix node."
      />
    )
  }

  const feeds = incomingFeeds(graph.nodes, graph.connections, node.id)
  const running = Boolean(flow.KEYS?.find((key) => key.NODE_ID === node.id)?.BLENDER_RUNNING)

  return (
    <MixerEditor
      flowId={flowId}
      flowName={flow.NAME}
      flowActive={Boolean(flow.ACTIVE)}
      running={running}
      node={node}
      feeds={feeds}
      refetch={() => flowQuery.refetch()}
    />
  )
}

function MixerEditor({
  flowId,
  flowName,
  flowActive,
  running,
  node,
  feeds,
  refetch,
}: {
  flowId: number
  flowName: string
  flowActive: boolean
  running: boolean
  node: FlowGraphNode
  feeds: LayerFeed[]
  refetch: () => Promise<unknown>
}) {
  const [confirmedNode, setConfirmedNode] = useState(() => normalizePassthrough(node))

  useEffect(() => {
    setConfirmedNode(normalizePassthrough(node))
  }, [node])

  const canControl = flowActive && running && Boolean(confirmedNode.live_controls)
  const control = useLiveControl<FlowGraphNode>({
    confirmed: confirmedNode,
    active: canControl,
    scopeKey: `${flowId}:${node.id}`,
    serialize: passthroughSettings,
    onConfigEdit: () => undefined,
    fallbackError: 'Could not apply mixer settings.',
    submit: async (patch) => {
      if (!appliesWithoutRestart(confirmedNode.live_controls, Object.keys(patch))) throw new Error('These settings are fixed while live. Stop the flow to change them.')
      try {
        const updated = normalizePassthrough(await applyNodeChange(
          'mixer_control',
          flowId,
          node.id,
          patch,
        ))
        setConfirmedNode(updated)
        await refetch()
        toast.success('Mixer layers applied live and saved')
        return updated
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Could not apply mixer settings.')
        throw error
      }
    },
  })

  const current = control.value
  const unavailableReason = !flowActive
    ? 'This flow is not armed. Arm it on the Flows tab before changing on-air layers.'
    : !confirmedNode.live_controls ? 'Live mixing is not enabled. Stop the flow to enable it.'
    : !running
      ? 'This mixer is not running. Controls become available once its blender starts.'
      : null
  const controlsDisabled = !canControl || control.busy

  return (
    <div className="space-y-4" data-testid="admin-live-mixer-detail">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/admin/live-mixer" search={{}}>← All live mixers</Link>
      </Button>

      <SectionCard
        title={`${flowName} · ${current.name || `Mixer #${current.id}`}`}
        description="Layer controls for the running two-feed blend. Feed connections stay locked while the flow is on air."
      >
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <StatePill tone={flowActive ? 'cyan' : 'muted'} label={flowActive ? 'Flow armed' : 'Flow idle'} />
          <StatePill tone={running ? 'cyan' : 'muted'} label={running ? 'Mixer running' : 'Mixer stopped'} />
          <StatePill
            tone="muted"
            label={confirmedNode.live_controls ? 'Applies without restart' : 'Restarts on apply'}
          />
        </div>

        {unavailableReason ? (
          <p role="note" className="mb-4 rounded-md border border-cyber-amber/30 bg-cyber-amber/5 px-3 py-2 text-xs text-cyber-amber">
            {unavailableReason} Edits are not sent while controls are unavailable.
          </p>
        ) : null}

        <LiveControlPanel
          control={control}
          active={canControl}
          unavailableReason={unavailableReason}
          retainOrphanInConfiguration={false}
          orphanedDescription="Live editing became unavailable before these changes were applied. Your draft is kept here and will be ready when the flow and mixer are running again, or you can discard it."
          description={confirmedNode.live_controls
            ? 'Changes are staged until Apply. Layout, position, visibility and swapping are applied without interrupting the mixer.'
            : 'This mixer has no live controls enabled. Stop the flow to change its settings.'}
          restrictions="Feed A and B are assigned by connection order and cannot be rewired here while live."
          applyLabel="Update live"
        >
          <div className="space-y-2">
            <p className="text-sm font-medium">Layout</p>
            <div className="flex flex-wrap gap-2">
              {([
                ['side_by_side', 'Side by side'],
                ['stacked', 'Stacked'],
                ['pip', 'Picture in picture'],
              ] as const).map(([layout, label]) => (
                <Button
                  key={layout}
                  type="button"
                  size="sm"
                  variant="outline"
                  aria-pressed={(current.layout ?? 'side_by_side') === layout}
                  className={(current.layout ?? 'side_by_side') === layout ? 'border-cyber-cyan text-cyber-cyan' : undefined}
                  disabled={controlsDisabled}
                  onClick={() => control.edit('layout', layout)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>

          {current.layout === 'pip' ? (
            <div className="space-y-2">
              <p className="text-sm font-medium">Thumbnail corner</p>
              <div className="flex flex-wrap gap-2">
                {([
                  ['tl', 'Top left'],
                  ['tr', 'Top right'],
                  ['bl', 'Bottom left'],
                  ['br', 'Bottom right'],
                ] as const).map(([corner, label]) => (
                  <Button
                    key={corner}
                    type="button"
                    size="sm"
                    variant="outline"
                    aria-pressed={(current.pip_corner ?? 'br') === corner}
                    className={(current.pip_corner ?? 'br') === corner ? 'border-cyber-cyan text-cyber-cyan' : undefined}
                    disabled={controlsDisabled}
                    onClick={() => control.edit('pip_corner', corner)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <RangeRow
              label="Split"
              field="split_ratio"
              value={current.split_ratio ?? 0.5}
              disabled={controlsDisabled}
              onChange={(value) => control.edit('split_ratio', value)}
              fmt={percent}
            />
          )}

          {feeds.length === 0 ? (
            <InlineEmpty message="This mixer has no connected feeds." />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {feeds.map((feed) => (
                <LayerCard
                  key={feed.slot}
                  feed={feed}
                  current={current}
                  disabled={controlsDisabled}
                  onToggle={(enabled) => control.edit(enableField(feed.slot), enabled)}
                  onPositionChange={(axis, value) => control.edit(`video_${axis}_${feed.slot}`, value)}
                />
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              disabled={controlsDisabled || feeds.length < 2}
              onClick={() => control.edit('video_swap', !current.video_swap)}
            >
              Swap A and B
            </Button>
            <span className="text-xs text-muted-foreground" aria-live="polite">
              {swapState(current)}
            </span>
          </div>
        </LiveControlPanel>
      </SectionCard>
    </div>
  )
}

function LayerCard({
  feed,
  current,
  disabled,
  onToggle,
  onPositionChange,
}: {
  feed: LayerFeed
  current: FlowGraphNode
  disabled: boolean
  onToggle: (enabled: boolean) => void
  onPositionChange: (axis: 'x' | 'y' | 'scale', value: number) => void
}) {
  const enabled = current[enableField(feed.slot)] ?? true
  const position = positionFor(current, feed.slot)
  const role = current.layout === 'pip'
    ? ((current.video_swap ? feed.slot === 'b' : feed.slot === 'a') ? 'Main layer' : 'Top layer')
    : `Position ${feed.slot.toUpperCase()}`

  return (
    <div className="space-y-3 rounded-lg border border-border/60 bg-background/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold" title={feed.name}>{feed.name}</h3>
          <p className="text-xs text-muted-foreground">
            Layer {feed.slot.toUpperCase()} · {role}
          </p>
        </div>
        <Switch
          checked={enabled}
          disabled={disabled}
          onCheckedChange={onToggle}
          aria-label={`${enabled ? 'Disable' : 'Enable'} layer ${feed.slot.toUpperCase()}`}
        />
      </div>
      <RangeRow
        label="X"
        field={`video_x_${feed.slot}`}
        value={position.x}
        disabled={disabled}
        onChange={(value) => onPositionChange('x', value)}
        fmt={percent}
      />
      <RangeRow
        label="Y"
        field={`video_y_${feed.slot}`}
        value={position.y}
        disabled={disabled}
        onChange={(value) => onPositionChange('y', value)}
        fmt={percent}
      />
      <RangeRow
        label="Size"
        field={`video_scale_${feed.slot}`}
        value={position.scale}
        disabled={disabled}
        onChange={(value) => onPositionChange('scale', value)}
        fmt={percent}
      />
    </div>
  )
}

function MixerMessage({
  title,
  message,
  onRetry,
}: {
  title: string
  message: string
  onRetry?: () => void
}) {
  return (
    <SectionCard title={title} description={message}>
      <div className="flex flex-wrap gap-2">
        {onRetry ? <Button size="sm" variant="outline" onClick={onRetry}>Retry</Button> : null}
        <Button size="sm" variant="ghost" asChild>
          <Link to="/admin/live-mixer" search={{}}>Back to live mixers</Link>
        </Button>
      </div>
    </SectionCard>
  )
}

/** 12 px sentence-case state mark. Green is reserved for on air (FlowStatePill). */
function StatePill({ tone, label }: { tone: 'cyan' | 'amber' | 'muted'; label: string }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium',
        tone === 'cyan' && 'border-cyber-cyan/40 bg-cyber-cyan/10 text-cyber-cyan',
        tone === 'amber' && 'border-cyber-amber/40 bg-cyber-amber/10 text-cyber-amber',
        tone === 'muted' && 'border-border/60 bg-muted/40 text-muted-foreground',
      )}
    >
      {label}
    </span>
  )
}

function mixersForFlow(flow: FlowListItem): MixerListEntry[] {
  const graphMixers = parseGraph(flow.GRAPH).nodes.filter((node) => node.type === 'passthrough')
  const live: FlowLiveSummary | null = flow.LIVE
  const activity = (live?.node_activity ?? []).filter((item) => item.node_type === 'passthrough')
  const activityById = new Map(activity.map((item) => [item.node_id, item]))
  const entries = graphMixers.map((node) => {
    const runtime = activityById.get(node.id)
    activityById.delete(node.id)
    return {
      nodeId: node.id,
      name: node.name || runtime?.node_name || `Mixer #${node.id}`,
      active: runtime?.active ?? false,
      blocked: runtime?.blocked ?? false,
    }
  })

  for (const runtime of activityById.values()) {
    entries.push({
      nodeId: runtime.node_id,
      name: runtime.node_name || `Mixer #${runtime.node_id}`,
      active: runtime.active,
      blocked: runtime.blocked,
    })
  }

  return entries.sort((a, b) => a.nodeId - b.nodeId)
}

function incomingFeeds(
  nodes: FlowGraphNode[],
  connections: FlowGraphConnection[],
  nodeId: number,
): LayerFeed[] {
  return connections
    .filter((connection) => connection.to === nodeId)
    .sort((a, b) => a.id - b.id)
    .slice(0, 2)
    .map((connection, index) => {
      const upstream = nodes.find((candidate) => candidate.id === connection.from)
      return {
        slot: index === 0 ? 'a' : 'b',
        name: upstream?.name || (upstream ? `${upstream.type} #${upstream.id}` : `Node #${connection.from}`),
      }
    })
}

function enableField(slot: Slot): 'video_enable_a' | 'video_enable_b' {
  return slot === 'a' ? 'video_enable_a' : 'video_enable_b'
}

function positionFor(node: FlowGraphNode, slot: Slot) {
  return slot === 'a'
    ? { x: node.video_x_a ?? 0, y: node.video_y_a ?? 0, scale: node.video_scale_a ?? 1 }
    : { x: node.video_x_b ?? 0, y: node.video_y_b ?? 0, scale: node.video_scale_b ?? 1 }
}

function percent(value: number): string {
  return `${Math.round(value * 100)}%`
}

/** The current order as text beside the Swap button. */
function swapState(node: FlowGraphNode): string {
  if (node.layout === 'pip') {
    return node.video_swap ? 'Now: B is the main layer, A is on top.' : 'Now: A is the main layer, B is on top.'
  }
  const first = node.layout === 'stacked' ? 'top' : 'left'
  const second = node.layout === 'stacked' ? 'bottom' : 'right'
  return node.video_swap ? `Now: B ${first}, A ${second}.` : `Now: A ${first}, B ${second}.`
}
